import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
} from "../../../../../node_modules/@mui/material/index";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { ReactComponent as SuccessIcon } from "../../../../scenes/stack/environment/icons/input_icons/success.svg";
import { isValidGUID } from "../../../../utils/utils";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import teams_search_bot from "./images/teams_search_bot.png";

const MICROSOFT_TEAMS_CODE_NAME = "microsoft_teams";

const textboxStyle = {
  "&& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,0,0,0.23) !important",
    borderWidth: "1px !important",
    transition: "none",
  },
};

export default function MicrosoftTeams() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { saveMicrosoftTenantID, sendMicrosoftTeamsTestMessage } = useAPI();
  const [tenantID, setTenantID] = useState("");
  const { isIntegrated, isIntegratedLoading, isIntegratedSuccess } =
    useIsIntegrated(MICROSOFT_TEAMS_CODE_NAME);
  const colors = useColors();

  const guideMode = searchParams.has("viewonly");

  const tenantIDMutation = useMutation({
    mutationFn: () => saveMicrosoftTenantID(tenantID),
    onError: (error) => {
      Toast.error("Error saving Microsoft Tenant ID");
    },
  });

  const testMicrosoftTeamsMutation = useMutation({
    mutationFn: sendMicrosoftTeamsTestMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      Toast.success("Microsoft Teams test message sent");
    },
    onError: (error) => {
      Toast.error("Error sending Microsoft Teams test message");
    },
  });

  return (
    <Guideline
      title={isIntegrated ? "" : "Connect Our Service to Microsoft Teams"}
      image={logos.MicrosoftTeamsLogo}
      iconBorderRadius={0}
      name="Microsoft Teams"
      content={
        isIntegratedLoading ? (
          <LoadingIcon size={25} />
        ) : isIntegrated && !guideMode ? (
          <Box>
            <Typography variant={"h4"} color={colors.text_primary}>
              Microsoft Teams is registered!
            </Typography>
          </Box>
        ) : isIntegratedSuccess ? (
          <Box display="flex" flexDirection="column" gap={4}>
            <Typography variant="body1" color={colors.text_primary}>
              You must be a Microsoft Teams administrator for your organization
              to complete this setup.
              <br />
              After setup, your daily updates will appear automatically in Teams
              — no further action needed.
            </Typography>
            <GuidelineStepByStep>
              <GuidelineStep title="Provide your Tenant ID">
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body1" color={colors.text_primary}>
                    The Tenant ID is a GUID (32 hexadecimal characters separated
                    by hyphens).
                  </Typography>
                  <TextWithExternalLink
                    text=" You can find it in your"
                    linkText="Microsoft Entra admin center"
                    href="https://entra.microsoft.com/"
                    tooltipText="https://entra.microsoft.com/"
                    variant="body1"
                  />

                  <TextField
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    fullWidth
                    type="text"
                    value={tenantID}
                    onChange={(e) => setTenantID(e.target.value)}
                    disabled={guideMode}
                    sx={textboxStyle}
                  />
                  <Box display="flex" gap={1} alignItems="center">
                    <ContainedButton
                      color="primary"
                      size="medium"
                      onClick={() => {
                        tenantIDMutation.mutate();
                      }}
                      loading={tenantIDMutation.isPending}
                      disabled={
                        !isValidGUID(tenantID) || tenantIDMutation.isSuccess
                      }
                    >
                      Save
                    </ContainedButton>
                    {tenantIDMutation.isSuccess && (
                      <>
                        <SvgToIcon IconLogo={SuccessIcon} width={16} />
                        <Typography variant="body2" color={colors.text_primary}>
                          Successfully saved
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </GuidelineStep>
              <GuidelineStep title="Install the Breeze Security Platform App in your team">
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body1" color={colors.text_primary}>
                    In your Microsoft Teams, go to the "Apps" section, search
                    for "Breeze" and add it to your team.
                  </Typography>
                  <Typography variant="body1" color={colors.text_primary}>
                    You should receive a welcome message from Breeze in your
                    selected channel.
                  </Typography>
                  <Typography variant="body1" color={colors.text_primary}>
                    If you didn't, please let us know.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: teams_search_bot,
                        altext: "teams search bot",
                      },
                    ]}
                    onExpanded={() => {}}
                  />
                </Box>
              </GuidelineStep>
              <GuidelineStep title="Test your integration">
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body1" color={colors.text_primary}>
                    Send a test message to you team
                  </Typography>
                </Box>
                <ContainedButton
                  color="primary"
                  size="medium"
                  onClick={() => {
                    testMicrosoftTeamsMutation.mutate();
                  }}
                  loading={testMicrosoftTeamsMutation.isPending}
                >
                  Send test message
                </ContainedButton>
              </GuidelineStep>
            </GuidelineStepByStep>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" color={colors.text_primary}>
              Error loading this page. Please try again later.
            </Typography>
          </Box>
        )
      }
    />
  );
}
